import type { ReactNode } from 'react';
import React from 'react';
import { Item, Root } from '@radix-ui/react-toggle-group';
import { twMerge } from 'tailwind-merge';

export const ToggleGroup = ({
  children,
  className,
  onValueChange,
  ...props
}: { children: ReactNode } & React.ComponentProps<typeof Root>) => {
  return (
    <Root
      className={twMerge('flex items-stretch rounded-md bg-white', className)}
      onValueChange={(value: string & string[]) => {
        // To ensure there's always a value selected
        if (value && onValueChange) onValueChange(value);
      }}
      {...props}
    >
      {children}
    </Root>
  );
};

export const ToggleGroupItem = ({
  children,
  className,
  disabled,
  ...props
}: { children: ReactNode } & React.ComponentProps<typeof Item> & {
    variant?: string;
  }) => {
  return (
    <Item
      className={twMerge(
        'flex w-full items-center justify-center rounded-md py-3',
        'font-title text-title-s font-medium text-base800',
        'border border-solid border-base300 duration-300',
        'data-[state=on]:bg-neutral900 data-[state=on]:text-white data-[state=on]:hover:text-white',
        'first:rounded-br-[0px] first:rounded-tr-[0px]', // first child border
        'last:rounded-bl-[0px] last:rounded-tl-[0px] last:border-l-0', // last child border
        '[&:not(:first-child):not(:last-child)]:rounded-[0px] [&:not(:first-child):not(:last-child)]:border-l-0', // in between child border
        'only:rounded-md only:border', // if only child
        !disabled && 'data-[state=off]:hover:shadow-tertiary',
        disabled && 'cursor-not-allowed bg-base100 text-base800 opacity-30',
        className,
      )}
      {...props}
    >
      {children}
    </Item>
  );
};

export const ChipToggleGroupItem = ({
  children,
  className,
  disabled,
  ...props
}: { children: ReactNode } & React.ComponentProps<typeof Item> & {
    variant?: string;
  }) => {
  return (
    <Item
      className={twMerge(
        'flex w-full items-center justify-center',
        'border border-solid border-base300 duration-300',
        'data-[state=on]:bg-neutral900 data-[state=on]:text-white data-[state=on]:hover:text-white',
        !disabled && 'data-[state=off]:hover:shadow-tertiary',
        disabled && 'cursor-not-allowed bg-base100 text-base800 opacity-30',
        className,
      )}
      {...props}
    >
      {children}
    </Item>
  );
};
